import { motion } from 'framer-motion';
import { AlertOctagon, Quote, Sparkles } from 'lucide-react';

/**
 * EngagedlyWorkshopBoard, the Post-Workshop Analysis section for the
 * Engagedly Homepage Redesign case study.
 *
 * The team ran a co-design workshop that produced dozens of sticky-notes.
 * Those notes were consolidated into three buckets:
 *   • Problems, what breaks today on the overview page
 *   • Statements, how users describe the page in their own words
 *   • Expectations, what they would like it to become
 *
 * This component renders each bucket as a colour-coded column of consolidated
 * "sticky-note" strips. It intentionally echoes the workshop feel (rotation,
 * dashed-tape pin, translucent tint) while staying legible at content density.
 */

const easeOut = [0.2, 0.8, 0.2, 1];

const buckets = [
    {
        key: 'problems',
        label: 'Problems',
        eyebrow: 'What breaks today',
        icon: AlertOctagon,
        tint: 'rgba(244, 63, 94, 0.10)',     // rose-500 @ 10%
        border: 'rgba(244, 63, 94, 0.35)',   // rose-500 @ 35%
        accent: '#fb7185',                   // rose-400
        note: 'rgba(244, 63, 94, 0.12)',
        items: [
            'Home page shows the same content for everyone.',
            'Announcements go stale within days.',
            'Widgets I don\u2019t use eat up the top fold.',
            'Navigation to my actual tools is buried.',
            'No visible sense of who I am on this page.',
            'Empty states feel like the page is broken.',
            'Layout doesn\u2019t adapt to what my day looks like.',
        ],
    },
    {
        key: 'statements',
        label: 'Statements',
        eyebrow: 'How users describe it',
        icon: Quote,
        tint: 'rgba(251, 191, 36, 0.10)',    // amber-400 @ 10%
        border: 'rgba(251, 191, 36, 0.35)',  // amber-400 @ 35%
        accent: '#fbbf24',                   // amber-400
        note: 'rgba(251, 191, 36, 0.12)',
        items: [
            'I bookmark my dashboard because home is too crowded.',
            'I want to open Engagedly and see MY day.',
            'The intranet before this had more warmth to it.',
            'Where\u2019s the widget for team wins?',
            'Announcements should feel like news, not archive.',
            'I check my chat before I check the portal.',
        ],
    },
    {
        key: 'expectations',
        label: 'Expectations',
        eyebrow: 'What it should become',
        icon: Sparkles,
        tint: 'rgba(16, 185, 129, 0.10)',    // emerald-500 @ 10%
        border: 'rgba(16, 185, 129, 0.35)',
        accent: '#34d399',                   // emerald-400
        note: 'rgba(16, 185, 129, 0.12)',
        items: [
            'Every widget should be personal by default.',
            'One place for my tasks, my team and my wins.',
            'Warm, human tone in every empty state.',
            'Onboarding checklist that adapts to my role.',
            'Fun, alive announcements with a fresh cadence.',
            'Tiny nudges when there\u2019s action to take.',
            'Let me hide widgets I don\u2019t need.',
        ],
    },
];

// A single sticky-note style strip. Tiny rotation and left "tape" mark give
// the workshop board texture without becoming decorative noise.
const StickyItem = ({ item, i, bucket }) => (
    <motion.li
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-4%' }}
        transition={{ duration: 0.45, delay: Math.min(i * 0.05, 0.35), ease: easeOut }}
        className="relative rounded-lg pl-5 pr-3.5 py-2.5 border font-sans-brand text-[13px] leading-[1.5] text-[var(--ink)]"
        style={{
            background: bucket.note,
            borderColor: bucket.border,
            transform: `rotate(${(i % 2 === 0 ? -1 : 1) * 0.3}deg)`,
        }}
    >
        {/* Left "washi-tape" mark */}
        <span
            aria-hidden
            className="absolute left-2 top-1/2 -translate-y-1/2 w-1 h-4 rounded-full"
            style={{ background: bucket.accent, opacity: 0.9 }}
        />
        {item}
    </motion.li>
);

const BucketColumn = ({ bucket, index }) => {
    const Icon = bucket.icon;
    return (
        <motion.article
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-6%' }}
            transition={{ duration: 0.55, delay: index * 0.08, ease: easeOut }}
            data-testid={`workshop-bucket-${bucket.key}`}
            className="relative rounded-2xl border p-5 md:p-6 flex flex-col gap-4 overflow-hidden"
            style={{
                background: bucket.tint,
                borderColor: bucket.border,
            }}
        >
            {/* Subtle top-accent bar */}
            <span
                aria-hidden
                className="absolute top-0 left-0 right-0 h-[3px]"
                style={{ background: bucket.accent }}
            />

            {/* Header */}
            <header className="flex items-start justify-between gap-3">
                <div>
                    <div
                        className="font-mono-brand text-[10px] uppercase tracking-[0.22em] mb-1"
                        style={{ color: bucket.accent }}
                    >
                        {bucket.eyebrow}
                    </div>
                    <h4 className="font-display text-2xl md:text-[28px] leading-[1.05] text-[var(--ink)]">
                        {bucket.label}
                        <span className="text-[var(--accent)]">.</span>
                    </h4>
                </div>
                <span
                    aria-hidden
                    className="inline-flex items-center justify-center w-8 h-8 rounded-full border shrink-0"
                    style={{
                        color: bucket.accent,
                        borderColor: bucket.border,
                        background: bucket.tint,
                    }}
                >
                    <Icon size={14} />
                </span>
            </header>

            {/* Count chip */}
            <div className="flex items-center gap-2">
                <span
                    className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-mono-brand text-[10px] uppercase tracking-[0.18em]"
                    style={{
                        background: bucket.note,
                        color: bucket.accent,
                        border: `1px solid ${bucket.border}`,
                    }}
                >
                    <span
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ background: bucket.accent }}
                    />
                    {String(bucket.items.length).padStart(2, '0')} consolidated notes
                </span>
            </div>

            {/* Sticky-note list */}
            <ul className="flex flex-col gap-2">
                {bucket.items.map((item, i) => (
                    <StickyItem key={i} item={item} i={i} bucket={bucket} />
                ))}
            </ul>
        </motion.article>
    );
};

export const EngagedlyWorkshopBoard = () => (
    <motion.figure
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-6%' }}
        transition={{ duration: 0.6, ease: easeOut }}
        data-testid="engagedly-workshop-board"
        className="mb-6 md:mb-8 rounded-2xl border border-[var(--line)] bg-[var(--surface)] overflow-hidden"
    >
        <header className="p-5 md:p-7 pb-3 border-b border-[var(--line)]">
            <div className="font-mono-brand text-[10.5px] uppercase tracking-[0.28em] text-[var(--ink-3)]">
                Workshop consolidation
            </div>
            <h4 className="mt-2 font-display text-3xl md:text-4xl text-[var(--ink)]">
                Problems &nbsp;·&nbsp; Statements &nbsp;·&nbsp; Expectations
                <span className="text-[var(--accent)]">.</span>
            </h4>
            <p className="mt-2 font-sans-brand text-[13.5px] md:text-[14.5px] leading-[1.6] text-[var(--ink-2)]">
                Every sticky note from the co-design workshop was clustered into
                three buckets. This board became the north-star reference for
                every widget and content decision that followed.
            </p>
        </header>

        <div className="p-4 md:p-6 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {buckets.map((bucket, i) => (
                <BucketColumn key={bucket.key} bucket={bucket} index={i} />
            ))}
        </div>
    </motion.figure>
);

export default EngagedlyWorkshopBoard;
